import { isValidMatriculeChecksum } from "@/functions/lu";
import { TranslationKey } from "@/models/i18n.model";
import { DateFormat } from "@/models/share.model";
import { format, parseISO } from "date-fns";

/**
 * Year is either:
 * - 19**
 * - 20**
 * - 39** (= 19**)
 * - 40** (= 20**)
 */
export const SSN_YEAR = "((?:19|20|39|40)\\d{2})";
export const SSN_YEAR_REGEXP = new RegExp(`^${SSN_YEAR}`);

/**
 * Month is either:
 * - 00
 * - 01 to 12
 */
export const SSN_MONTH = "(0[1-9]|1[0-2]|0{2})";
export const SSN_MONTH_REGEXP = new RegExp(`^${SSN_YEAR}${SSN_MONTH}`);

/**
 * Day is either:
 * - 00
 * - 01 to 31
 */
export const SSN_DAY = "(3[01]|[12][0-9]|0[1-9]|0{2})";
export const SSN_DAY_REGEXP = new RegExp(`^${SSN_YEAR}${SSN_MONTH}${SSN_DAY}`);

/**
 * Random generated digits from the security number
 */
export const SSN_RANDOM = "(\\d{3})";

/**
 * Control digits from the security number
 */
export const SSN_CONTROL = "(\\d{2})";
export const SSN_REGEXP = new RegExp(`^${SSN_YEAR}${SSN_MONTH}${SSN_DAY}${SSN_RANDOM}${SSN_CONTROL}$`);
export const SSN_DATE_LENGTH = 8;
export const SSN_LENGTH = 13;

/**
 * Check if input is either or birthdate or ssn.
 * 
 * @param ssn string, birthdate or ssn
 * @returns True if a valid birthdate or ssn with valid birthdate, false otherwise
 */
export const isValidBirthDateOrSsn = (ssn: string): boolean => {
    const ssnLength = ssn.length;

    if (ssnLength < SSN_LENGTH) {
        const matchDateFormat = SSN_DAY_REGEXP.exec(ssn);
        if (matchDateFormat) {
            return Boolean(convertToBirthDate(matchDateFormat[0]));
        }
    } else if (ssnLength === SSN_LENGTH) {
        const matchFormat = SSN_REGEXP.exec(ssn);
        if (matchFormat) {
            return Boolean(convertToBirthDate(matchFormat[0]));
        }
    }
    
    return false;
}

/**
 * Convert the extracted date from the SSN to a valid birthdate
 * @param ssn string matching SSN_DAY_REGEXP
 * @returns Date if birthdate is a valid date, null otherwise
 */
export const convertToBirthDate = (ssn: string): Date | null => {
    let year = ssn.substring(0, 4);
    let month = ssn.substring(4, 6);
    let day = ssn.substring(6, 8);

    if (year.startsWith('3')) {
        year = year.replace('3', '1');
    } else if (year.startsWith('4')) {
        year = year.replace('4', '2');
    }

    if (month === '00') {
        month = '01';
    }

    if (day === '00') {
        day = '01';
    }

    const isoDate = parseISO(`${year}${month}${day}`);
    if (Number.isNaN(isoDate.getDate())) {
        return null;
    }

    return isoDate;
}

/**
 * Convert value to dd/MM/yyyy format
 * 
 * @param value
 * @returns formatted date if value is valid, undefined otherwise
 */
export const convertToFormattedBirthdate = (value?: string): string | undefined => {
    if (!value || value.length !== 8) {
        return undefined;
    }

    const birthDate = convertToBirthDate(value);
    if (birthDate) {
        return format(birthDate, DateFormat.FRONT_LONG);
    }
}

/**
 * Check if a security number is valid and returns a TranslationKey if it contains an error
 * Taken from src/functions/lu.js
 * 
 * @param ssn - String of digits representing the National Security Number (SSN)
 * @returns TranslationKey if not valid, undefined otherwise
 */
export function getInvalidSsnMessage(ssn: string): TranslationKey | undefined {
    if (ssn.length > SSN_LENGTH) {
        return "patient.search.tooLong";
    }

    if (!isValidBirthDateOrSsn(ssn)) {
        return "patient.search.invalidDate";
    }

    if (ssn.length === SSN_LENGTH && !isValidMatriculeChecksum(ssn)) {
        return "patient.search.invalidChecksum";
    }

    const validLengths = [SSN_DATE_LENGTH, SSN_LENGTH];
    if (!validLengths.includes(ssn.length)) {
        return "patient.search.newPatient";
    }
}